import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Error as ErrorIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { getPublicSalarySlip } from '@/services/HrService/PayrollService';
import { getCompanyInfo } from '@/services/CompanyInfoService';

// Shared cell styles
const labelCell = {
  border: '1px solid #bbb',
  py: 0.8,
  px: 1.5,
  bgcolor: '#f5f5f5',
  color: '#333',
  fontSize: '0.82rem',
  fontWeight: 500,
  width: '20%',
};

const valueCell = {
  border: '1px solid #bbb',
  py: 0.8,
  px: 1.5,
  color: '#111',
  fontSize: '0.82rem',
  width: '30%',
  borderBottom: '1px solid #888',
};

// Same file the PDF header uses (nucleus-api/public/assets/logos/company-logo.png),
// so this view and the downloaded slip show the identical mark.
const COMPANY_LOGO_SRC = '/assets/logos/company-logo.png';

const ViewPublicSalarySlip: React.FC = () => {
  const { id, token } = useParams<{ id: string; token: string }>();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [salarySlip, setSalarySlip] = useState<any>(null);
  const [companyInfo, setCompanyInfo] = useState<any>(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!id || !token) {
        setError('Invalid QR code');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch both salary slip and company info in parallel
        const [salarySlipResponse, companyResponse] = await Promise.all([
          getPublicSalarySlip(id, token),
          getCompanyInfo()
        ]);

        if (salarySlipResponse.success && salarySlipResponse.data) {
          setSalarySlip(salarySlipResponse.data);
        } else {
          setError(salarySlipResponse.error?.message || 'Failed to load salary slip');
        }

        if (companyResponse.success && companyResponse.data) {
          setCompanyInfo(companyResponse.data);
        }
      } catch (err: any) {
        console.error('Error loading salary slip:', err);
        setError(err.message || 'Failed to load salary slip. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, token]);

  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fa',
      }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={60} sx={{ color: '#1976d2' }} />
          <Typography variant="h6" sx={{ mt: 3, color: '#333', fontWeight: 500 }}>
            Loading Salary Slip...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{
        minHeight: '100vh',
        bgcolor: '#f5f7fa',
        py: 6,
      }}>
        <Container maxWidth="md">
          <Box sx={{
            bgcolor: '#fff',
            borderRadius: 3,
            overflow: 'hidden',
            border: '1px solid #ddd',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}>
            <Box sx={{ textAlign: 'center', py: 8, px: 4 }}>
              <ErrorIcon sx={{ fontSize: 100, color: '#f44336', mb: 3 }} />
              <Typography variant="h4" fontWeight="bold" sx={{ color: '#333', mb: 2 }}>
                Unable to Load Salary Slip
              </Typography>
              <Alert severity="error" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
                {error}
              </Alert>
              <Typography variant="body1" sx={{ color: '#666', mb: 3 }}>
                This salary slip could not be loaded. Possible reasons:
              </Typography>
              <Box sx={{ textAlign: 'left', maxWidth: 400, mx: 'auto', mb: 4 }}>
                <Typography variant="body2" sx={{ color: '#666', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f44336', mr: 2 }} />
                  QR code was not scanned correctly
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f44336', mr: 2 }} />
                  The link has expired
                </Typography>
                <Typography variant="body2" sx={{ color: '#666', mb: 1.5, display: 'flex', alignItems: 'center' }}>
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f44336', mr: 2 }} />
                  The document has been tampered with
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#999', display: 'block' }}>
                If you believe this is an error, please contact HR department
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>
    );
  }

  if (!salarySlip) return null;

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const monthNum = String(date.getMonth() + 1).padStart(2, '0');
    return `${day}/${monthNum}/${date.getFullYear()}`;
  };

  const earnings = salarySlip.earnings || [];
  const deductions = salarySlip.deductions || [];

  // Pad arrays so table rows align
  const maxRows = Math.max(earnings.length + 1, deductions.length); // +1 for Basic Salary row
  const earningsRows = [null, ...earnings]; // null = Basic Salary row
  while (earningsRows.length < maxRows) earningsRows.push(null);
  const deductionsRows = [...deductions];
  while (deductionsRows.length < maxRows) deductionsRows.push(null);

  const generateQRCodeURL = () => {
    if (!salarySlip) return '';
    const frontendURL = process.env.REACT_APP_FRONTEND_URL || window.location.origin;
    return `${frontendURL}/salary-slip/${id}/${token}`;
  };

  return (
    <Box sx={{
      minHeight: '100vh',
      bgcolor: '#f5f7fa',
      py: 4,
    }}>
      <Container maxWidth="md">
        {/* Salary Slip Document */}
        <Box
          sx={{
            bgcolor: '#ffffff',
            color: '#111',
            p: { xs: 3, sm: 5 },
            fontFamily: '"Times New Roman", Times, serif',
            border: '1px solid #ddd',
            boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          }}
        >
          {/* ── Company Header ── */}
          {/* Logo top-left with the details centred across the full width,
              mirroring the PDF header in utils/pdfGenerator.js. The symmetric px
              keeps that centring while stopping the text running under the logo
              on narrow screens — an absolutely positioned child is placed against
              the padding box, so the logo itself stays pinned to the edge. */}
          <Box sx={{ position: 'relative', textAlign: 'center', mb: 2, minHeight: 62, px: '70px' }}>
            <Box
              component="img"
              src={COMPANY_LOGO_SRC}
              alt={companyInfo?.company_name || 'Company logo'}
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: 62,
                height: 62,
                objectFit: 'contain',
              }}
            />
            <Typography
              sx={{
                fontFamily: '"Times New Roman", Times, serif',
                fontWeight: 'bold',
                fontSize: '1.35rem',
                letterSpacing: '1.5px',
                color: '#111',
                textTransform: 'uppercase',
              }}
            >
              {companyInfo?.company_name || 'MKRAFT CAPABILITY SOLUTIONS LLP'}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#444', mt: 0.3 }}>
              {companyInfo?.company_address || 'Electronic City Phase 2, Bangalore, Karnataka'} -{' '}
              {companyInfo?.pincode || '560100'}
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: '#444' }}>
              Phone: {companyInfo?.phone_number || '+91-XXXXXXXXXX'} | Email:{' '}
              {companyInfo?.email || 'hr@company.com'}
            </Typography>
          </Box>

          {/* ── Salary Slip Title ── */}
          <Typography
            sx={{
              fontFamily: '"Times New Roman", Times, serif',
              fontWeight: 'bold',
              fontSize: '1rem',
              color: '#111',
              mt: 3,
              mb: 2,
            }}
          >
            Salary Slip - [{salarySlip.payroll_period}]
          </Typography>

          {/* ── Employee Information ── */}
          <Typography
            sx={{
              fontFamily: '"Times New Roman", Times, serif',
              fontWeight: 'bold',
              fontStyle: 'italic',
              fontSize: '0.9rem',
              color: '#222',
              mb: 0.5,
            }}
          >
            Employee Information
          </Typography>
          <Table
            size="small"
            sx={{
              mb: 2,
              borderCollapse: 'collapse',
              '& td': { border: '1px solid #bbb', py: 0.8, px: 1.5, fontSize: '0.82rem' },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell sx={{ ...labelCell }}>Employee Name</TableCell>
                <TableCell sx={{ ...valueCell }}>{salarySlip.employee_name}</TableCell>
                <TableCell sx={{ ...labelCell }}>PAN Number</TableCell>
                <TableCell sx={{ ...valueCell }}>{salarySlip.pan_number || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ ...labelCell }}>Bank Name</TableCell>
                <TableCell sx={{ ...valueCell }}>{salarySlip.bank_name || '—'}</TableCell>
                <TableCell sx={{ ...labelCell }}>Account Number</TableCell>
                <TableCell sx={{ ...valueCell }}>{salarySlip.account_number || '—'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ ...labelCell }}>UAN Number</TableCell>
                <TableCell sx={{ ...valueCell, width: '80%' }} colSpan={3}>
                  {salarySlip.uan_number || '—'}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* ── Pay Period Details ── */}
          <Typography
            sx={{
              fontFamily: '"Times New Roman", Times, serif',
              fontWeight: 'bold',
              fontStyle: 'italic',
              fontSize: '0.9rem',
              color: '#222',
              mb: 0.5,
            }}
          >
            Pay Period Details
          </Typography>
          <Table
            size="small"
            sx={{
              mb: 2,
              borderCollapse: 'collapse',
              '& td': { border: '1px solid #bbb', py: 0.8, px: 1.5, fontSize: '0.82rem' },
            }}
          >
            <TableBody>
              <TableRow>
                <TableCell sx={{ ...labelCell, width: '20%' }}>Pay Period</TableCell>
                <TableCell sx={{ ...valueCell, width: '80%' }} colSpan={3}>
                  {formatDate(salarySlip.pay_period_start)} to {formatDate(salarySlip.pay_period_end)}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ ...labelCell }}>Pay Date</TableCell>
                <TableCell sx={{ ...valueCell }} colSpan={3}>
                  {formatDate(salarySlip.pay_date)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* ── Compensation Breakdown ── */}
          <Typography
            sx={{
              fontFamily: '"Times New Roman", Times, serif',
              fontWeight: 'bold',
              fontStyle: 'italic',
              fontSize: '0.9rem',
              color: '#222',
              mb: 0.5,
            }}
          >
            Compensation Breakdown
          </Typography>
          <Table
            size="small"
            sx={{
              mb: 3,
              borderCollapse: 'collapse',
              '& td, & th': { border: '1px solid #bbb', py: 0.8, px: 1.5, fontSize: '0.82rem' },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', width: '30%' }}>Earnings</TableCell>
                <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', width: '20%' }}>Amount (Rs.)</TableCell>
                <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', width: '30%' }}>Deductions</TableCell>
                <TableCell sx={{ bgcolor: '#e0e0e0', fontWeight: 'bold', width: '20%' }}>Amount (Rs.)</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {/* Basic Salary always first */}
              <TableRow>
                <TableCell>Basic Salary</TableCell>
                <TableCell>{salarySlip.gross_salary?.toLocaleString() || '0'}</TableCell>
                <TableCell>{deductionsRows[0]?.label || ''}</TableCell>
                <TableCell>
                  {deductionsRows[0] ? deductionsRows[0].amount.toLocaleString() : ''}
                </TableCell>
              </TableRow>

              {/* Remaining rows */}
              {Array.from({ length: maxRows - 1 }).map((_, i) => {
                const earning = earningsRows[i + 1]; // skip index 0 (Basic)
                const deduction = deductionsRows[i + 1];
                return (
                  <TableRow key={i}>
                    <TableCell>{earning?.label || ''}</TableCell>
                    <TableCell>{earning ? earning.amount.toLocaleString() : ''}</TableCell>
                    <TableCell>{deduction?.label || ''}</TableCell>
                    <TableCell>{deduction ? deduction.amount.toLocaleString() : ''}</TableCell>
                  </TableRow>
                );
              })}

              {/* Totals row */}
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Earnings</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{salarySlip.total_earnings?.toLocaleString() || '0'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Total Deductions</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>{salarySlip.total_deductions?.toLocaleString() || '0'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>

          {/* ── Net Salary ── */}
          <Box sx={{ mb: 4 }}>
            <Typography
              sx={{
                fontFamily: '"Times New Roman", Times, serif',
                fontWeight: 'bold',
                fontSize: '1.05rem',
                color: '#111',
              }}
            >
              Net Salary: Rs. {salarySlip.net_salary?.toLocaleString() || '0'}
            </Typography>
          </Box>

          {/* ── Signature & Footer ── */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mt: 2 }}>
            {/* Left: Signature + footer note */}
            <Box>
              <Typography sx={{ fontSize: '0.82rem', color: '#444', mb: 4 }}>Signature</Typography>
              <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
                For any queries, please contact the HR department.
              </Typography>
            </Box>

            {/* Right: QR Code */}
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  p: '6px',
                  background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                  borderRadius: '10px',
                  boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
                }}
              >
                <Box sx={{ p: '5px', bgcolor: '#fff', borderRadius: '6px' }}>
                  <QRCodeSVG
                    value={generateQRCodeURL()}
                    size={80}
                    level="H"
                    includeMargin={false}
                    bgColor="#ffffff"
                    fgColor="#000000"
                    imageSettings={{
                      src: '/assets/logos/transparent_m.PNG',
                      x: undefined,
                      y: undefined,
                      height: 24,
                      width: 24,
                      excavate: true,
                    }}
                  />
                </Box>
              </Box>
              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, letterSpacing: '0.5px', color: '#333' }}>
                SCAN TO VIEW
              </Typography>
              <Typography sx={{ fontSize: '0.6rem', color: '#888' }}>Salary Slip Online</Typography>
            </Box>
          </Box>
        </Box>

        {/* Bottom Branding */}
        <Box sx={{ textAlign: 'center', mt: 4, pb: 4 }}>
          <Typography variant="caption" sx={{ color: '#999' }}>
            © {new Date().getFullYear()} {companyInfo?.company_name || 'MKRAFT CAPABILITY SOLUTIONS LLP'}. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ViewPublicSalarySlip;
